import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const TIPOS_ENTRADA = ['entrada', 'entrada_desplazamiento'];

const TIPO_LABEL = {
  entrada:                { txt: 'Entrada',                 cls: 'bg-green-100 text-green-800' },
  entrada_desplazamiento: { txt: '🚗 Entrada (Desplaz.)',   cls: 'bg-teal-100 text-teal-800' },
  salida:                 { txt: 'Salida',                  cls: 'bg-red-100 text-red-800' },
  inicio_pausa:           { txt: 'Pausa',                   cls: 'bg-yellow-100 text-yellow-800' },
  fin_pausa:              { txt: 'Fin Pausa',                cls: 'bg-blue-100 text-blue-800' },
};

function calcHoras(fichajes) {
  let total = 0, entrada = null, pausaIni = null, pausaAcum = 0;
  for (const f of [...fichajes].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))) {
    const ts = new Date(f.timestamp).getTime();
    if (TIPOS_ENTRADA.includes(f.tipo)) { entrada = ts; pausaAcum = 0; pausaIni = null; }
    else if (f.tipo === 'inicio_pausa' && entrada) { pausaIni = ts; }
    else if (f.tipo === 'fin_pausa' && pausaIni) { pausaAcum += ts - pausaIni; pausaIni = null; }
    else if (f.tipo === 'salida' && entrada) { total += (ts - entrada) - pausaAcum; entrada = null; pausaAcum = 0; }
  }
  if (entrada) {
    const now = Date.now();
    total += (now - entrada) - pausaAcum - (pausaIni ? now - pausaIni : 0);
  }
  const h = Math.floor(total / 3600000);
  const m = Math.round((total % 3600000) / 60000);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export default function Historial() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [fichajes, setFichajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/fichajes/mis-fichajes?mes=${mes}&anio=${anio}`)
      .then((r) => setFichajes(r.data.fichajes))
      .catch(() => setFichajes([]))
      .finally(() => setLoading(false));
  }, [mes, anio]);

  const fmtTime = (ts) =>
    new Date(ts).toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' });

  const fmtDia = (ts) =>
    new Date(ts).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', weekday: 'short', day: 'numeric', month: 'short' });

  // Group by day
  const porDia = {};
  for (const f of fichajes) {
    const dia = new Date(f.timestamp).toLocaleDateString('es-ES', {
      timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit'
    });
    if (!porDia[dia]) porDia[dia] = [];
    porDia[dia].push(f);
  }
  const dias = Object.entries(porDia).sort((a, b) => b[0].localeCompare(a[0]));

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        <h3 className="font-semibold text-gray-700 mb-3">Mis Fichajes</h3>
        <div className="flex gap-2">
          <select
            className="input flex-1"
            value={mes}
            onChange={(e) => setMes(Number(e.target.value))}
          >
            {meses.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select
            className="input w-28"
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400">Cargando...</div>
      ) : dias.length === 0 ? (
        <div className="card text-center py-8 text-gray-400">
          <div className="text-3xl mb-2">📋</div>
          <div>No hay registros para este período</div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {dias.map(([dia, fDia]) => {
            const entrada = fDia.find(f => TIPOS_ENTRADA.includes(f.tipo));
            const salida = fDia.find(f => f.tipo === 'salida');
            const horas = calcHoras(fDia);
            const isOpen = expanded === dia;

            return (
              <div key={dia} className="card overflow-hidden">
                <button
                  onClick={() => setExpanded(isOpen ? null : dia)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div>
                    <div className="font-semibold text-gray-800 capitalize">{fmtDia(fDia[0].timestamp)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {entrada ? `Entrada: ${fmtTime(entrada.timestamp)}` : 'Sin entrada'}
                      {salida ? ` · Salida: ${fmtTime(salida.timestamp)}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-mgh-blue-900">{horas}</span>
                    <span className={`transition-transform ${isOpen ? 'rotate-180' : ''} text-gray-400`}>▾</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    {fDia.map((f) => {
                      const tl = TIPO_LABEL[f.tipo];
                      return (
                        <div key={f.id} className="flex items-center justify-between">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tl.cls}`}>
                            {tl.txt}
                          </span>
                          <span className="text-sm font-mono text-gray-700">{fmtTime(f.timestamp)}</span>
                          {f.editado ? <span className="text-xs text-amber-600">✎ editado</span> : <span />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
